import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { LogoWithText } from "../components/Logo";
import { updatePageTitle } from "../utils/titleUtils";
import { RESET_PASSWORD } from "../api/apiFunction/authServices";
import AuthShowcase from "../components/pages/auth/AuthShowcase";

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    updatePageTitle("Reset Password");

    // Redirect if no token or email
    if (!token || !email) {
      toast.error("Invalid reset link");
      navigate("/login");
    }
  }, [token, email, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords
    if (!formData.newPassword || !formData.confirmPassword) {
      toast.error("Both password fields are required");
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const response = await RESET_PASSWORD({
        token,
        email,
        newPassword: formData.newPassword,
      });

      if (response.status === 200) {
        toast.success(
          "Password reset successfully! Please login with your new password."
        );
        navigate("/login");
      } else {
        const errorMessage =
          response?.data?.error ||
          response?.data?.message ||
          "Failed to reset password";
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to reset password";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="h-screen bg-bg-50 flex overflow-hidden">
      {/* Left Side - Auth Form */}
      <div className="w-full lg:w-1/2 bg-bg-50 p-4 lg:p-8">
        <div className="bg-bg-70 rounded-3xl h-full flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Logo */}
            <div className="text-center">
              <LogoWithText className="h-8 w-auto mx-auto mb-8" />
              <div className="leading-tight">
                <h2 className="text-xl font-semibold text-fg-50 mb-1">
                  Reset your password and
                </h2>
                <p className="text-xl font-semibold text-fg-50">
                  secure your account.
                </p>
              </div>
            </div>

            {/* Form */}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    required
                    className="w-full px-4 py-2 pr-12 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                    placeholder="••••••••"
                    value={formData.newPassword}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={loading}
                  >
                    {showNewPassword ? (
                      <EyeOff
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    ) : (
                      <Eye
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    className="w-full px-4 py-2 pr-12 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? (
                      <EyeOff
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    ) : (
                      <Eye
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 rounded-lg text-sm font-medium text-white dark:text-black ${
                  loading
                    ? "bg-ac-02 bg-opacity-25 cursor-not-allowed"
                    : "bg-ac-02 hover:bg-ac-01"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>

            {/* Back to Login Link */}
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-fg-60 hover:text-fg-50 font-medium"
              >
                ← Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Showcase */}
      <AuthShowcase />
    </div>
  );
};

export default ResetPassword;
